import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-hot-toast'
import { apiFetch } from '@/utils/api'

export type RunningScan = {
  scanId: string
  target: string | null
  status: string
  scanProfile: string | null
  sessionId: string | null
  userId: string | null
  userEmail: string | null
  userRole: string | null
  orgId: string | null
  startTime: string | null
  pid: number | null
  processInfo?: {
    pid: number | null
    startTime?: string | null
    target?: string | null
    scanProfile?: string | null
  } | null
  sqlmapContext?: {
    pid?: number | null
    context?: Record<string, unknown>
    userId?: string | null
    scanProfile?: string | null
    startTime?: string | null
  } | null
  preScanStages?: {
    harness?: HarnessStageSummary
    payloadLab?: PayloadStageSummary
    updatedAt?: string
  } | null
}

export type RunningScansSnapshot = {
  items: RunningScan[]
  totals: {
    database: number
    processes: number
    sqlmap: number
  }
}

const PAGE_SIZE_OPTIONS = [10, 20, 50]
const REFRESH_INTERVAL_MS = 15000

type TerminateHandler = (scan: RunningScan) => Promise<void>

type RunningScansPanelProps = {
  onTerminate?: TerminateHandler
}

type StageStatus = 'pending' | 'running' | 'completed' | 'partial' | 'failed' | 'skipped' | 'disabled'

type HarnessStageSummary = {
  status: StageStatus
  totalParameters?: number
  completed?: number
  reflections?: number
  errors?: Array<{ parameter?: string; error?: string }>
  runs?: Array<{ parameter?: string; reflectionHits?: number; diffLength?: number }>
}

type PayloadStageSummary = {
  status: StageStatus
  totalAttempts?: number
  successes?: number
  wafBlocked?: number
}

const formatDateTime = (iso: string | null | undefined) => {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString()
}

const formatDuration = (iso: string | null | undefined) => {
  if (!iso) return '—'
  const start = new Date(iso).getTime()
  if (!Number.isFinite(start)) return '—'
  const diffSeconds = Math.max(0, Math.round((Date.now() - start) / 1000))
  const hours = Math.floor(diffSeconds / 3600)
  const minutes = Math.floor((diffSeconds % 3600) / 60)
  const seconds = diffSeconds % 60
  const parts = []
  if (hours > 0) parts.push(`${hours}h`)
  if (minutes > 0 || hours > 0) parts.push(`${minutes}m`)
  parts.push(`${seconds}s`)
  return parts.join(' ')
}

const matchSearch = (scan: RunningScan, query: string) => {
  if (!query) return true
  const q = query.toLowerCase()
  return [
    scan.scanId,
    scan.target,
    scan.sessionId,
    scan.userId,
    scan.userEmail,
    scan.orgId,
    scan.status,
    scan.scanProfile
  ]
    .filter(Boolean)
    .some(value => value!.toLowerCase().includes(q))
}

export function RunningScansPanel({ onTerminate }: RunningScansPanelProps) {
  const [snapshot, setSnapshot] = useState<RunningScansSnapshot | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0])
  const [terminatingScanId, setTerminatingScanId] = useState<string | null>(null)

  const fetchSnapshot = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await apiFetch<RunningScansSnapshot>('/api/admin/scans/running')
      setSnapshot(data)
      return true
    } catch (err: any) {
      const message = err?.message || 'Failed to load running scans'
      setError(message)
      toast.error(message)
      return false
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchSnapshot()
    const timer = setInterval(fetchSnapshot, REFRESH_INTERVAL_MS)
    return () => clearInterval(timer)
  }, [fetchSnapshot])

  useEffect(() => {
    setPage(1)
  }, [search, pageSize])

  const filteredItems = useMemo(() => {
    if (!snapshot) return []
    const items = snapshot.items.filter(item => matchSearch(item, search))
    return items.sort((a, b) => {
      const timeA = a.startTime ? new Date(a.startTime).getTime() : 0
      const timeB = b.startTime ? new Date(b.startTime).getTime() : 0
      return timeB - timeA
    })
  }, [snapshot, search])

  const pageCount = Math.max(1, Math.ceil(filteredItems.length / pageSize))
  const currentPage = Math.min(page, pageCount)
  const pagedItems = filteredItems.slice((currentPage - 1) * pageSize, currentPage * pageSize)

  const handleManualRefresh = async () => {
    const ok = await fetchSnapshot()
    if (ok) toast.success('Running scans refreshed')
  }

  const handleTerminate = async (scan: RunningScan) => {
    if (!onTerminate) return
    try {
      setTerminatingScanId(scan.scanId)
      await onTerminate(scan)
      await fetchSnapshot()
    } catch (err: any) {
      const message = err?.message || 'Failed to terminate scan'
      toast.error(message)
    } finally {
      setTerminatingScanId(null)
    }
  }

  const handleRerun = async (scan: RunningScan, mode: 'harness' | 'payload-lab') => {
    try {
      await apiFetch('/api/param-harness/rerun', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scanId: scan.scanId, mode })
      })
      toast.success(`Rerun ${mode} triggered`)
      handleManualRefresh()
    } catch (err: any) {
      toast.error(err?.message || 'Rerun failed')
    }
  }

  return (
    <div className="bg-gray-800 p-4 rounded-lg border border-gray-700 space-y-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-blue-200">Active Scans</h2>
          <p className="text-sm text-gray-400">Live view of running scans across the platform.</p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
          <input
            type="search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search target, user, session…"
            className="bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 focus:border-blue-500 focus:outline-none"
          />
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-xs text-gray-400" htmlFor="running-scans-page-size">Page size</label>
            <select
              id="running-scans-page-size"
              value={pageSize}
              onChange={e => setPageSize(Number(e.target.value))}
              className="bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm text-gray-200 w-full sm:w-auto"
            >
              {PAGE_SIZE_OPTIONS.map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>
          <button
            onClick={handleManualRefresh}
            disabled={loading}
            className="px-3 py-2 rounded border border-blue-600 bg-blue-700 text-sm text-white disabled:opacity-60 disabled:cursor-not-allowed w-full sm:w-auto"
          >
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div className="text-sm text-red-400">{error}</div>
      )}

      {snapshot && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard label="DB active" value={snapshot.totals.database} subtitle="Rows flagged as running" />
          <StatCard label="Processes tracked" value={snapshot.totals.processes} subtitle="In-memory workers" />
          <StatCard label="SQLMap sessions" value={snapshot.totals.sqlmap} subtitle="Sessions reporting active" />
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm text-gray-200">
          <thead>
            <tr className="bg-gray-900 text-left text-xs uppercase tracking-wide text-gray-400">
              <th className="px-3 py-2 font-semibold text-gray-300">Target</th>
              <th className="px-3 py-2 font-semibold text-gray-300">User</th>
              <th className="px-3 py-2 font-semibold text-gray-300">Session</th>
              <th className="px-3 py-2 font-semibold text-gray-300">Started</th>
              <th className="px-3 py-2 font-semibold text-gray-300">Duration</th>
              <th className="px-3 py-2 font-semibold text-gray-300">Pre-scan stages</th>
              <th className="px-3 py-2 font-semibold text-gray-300">Status</th>
              <th className="px-3 py-2 font-semibold text-gray-300 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (!snapshot || snapshot.items.length === 0) && (
              <tr>
                <td colSpan={8} className="px-3 py-4 text-center text-gray-400">Loading…</td>
              </tr>
            )}

            {!loading && pagedItems.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-4 text-center text-gray-500">No running scans found.</td>
              </tr>
            )}

            {pagedItems.map(scan => (
              <tr key={scan.scanId} className="border-b border-gray-700/60 last:border-none hover:bg-gray-900/60">
                <td className="px-3 py-2 align-top">
                  <div className="font-medium text-gray-100 break-words" title={scan.target || '—'}>
                    {scan.target || '—'}
                  </div>
                  <div className="text-xs text-gray-500">Profile: {scan.scanProfile || 'default'}</div>
                </td>
                <td className="px-3 py-2 align-top">
                  <div className="text-gray-100 break-words">{scan.userEmail || scan.userId || '—'}</div>
                  <div className="text-xs text-gray-500">Org: {scan.orgId || 'n/a'}</div>
                </td>
                <td className="px-3 py-2 align-top">
                  <div className="text-xs text-gray-400 break-all" title={scan.sessionId || '—'}>{scan.sessionId || '—'}</div>
                  {scan.pid != null && (
                    <div className="text-[11px] text-gray-500">PID: {scan.pid}</div>
                  )}
                </td>
                <td className="px-3 py-2 align-top text-xs text-gray-300 leading-snug">
                  <div>{formatDateTime(scan.startTime)}</div>
                </td>
                <td className="px-3 py-2 align-top text-xs text-gray-300">
                  <div>{formatDuration(scan.startTime)}</div>
                </td>
                <td className="px-3 py-2 align-top">
                  <div className="flex flex-col gap-1.5">
                    <StageBadge
                      label="Harness"
                      stage={scan.preScanStages?.harness}
                      summary={formatHarnessSummary(scan.preScanStages?.harness)}
                      onRerun={() => handleRerun(scan, 'harness')}
                    />
                    <StageBadge
                      label="Payload Lab"
                      stage={scan.preScanStages?.payloadLab}
                      summary={formatPayloadSummary(scan.preScanStages?.payloadLab)}
                      onRerun={() => handleRerun(scan, 'payload-lab')}
                    />
                  </div>
                </td>
                <td className="px-3 py-2 align-top">
                  <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-semibold ${scan.status === 'running' ? 'bg-green-900 text-green-200' : 'bg-yellow-900 text-yellow-200'}`}>
                    {scan.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-right align-top">
                  <div className="flex flex-wrap justify-end gap-2">
                    <button
                      className="px-3 py-1 rounded border border-red-600 text-red-200 hover:bg-red-600/20 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => handleTerminate(scan)}
                      disabled={!onTerminate || loading || terminatingScanId === scan.scanId}
                    >
                      {terminatingScanId === scan.scanId ? 'Terminating…' : 'Terminate'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between text-sm text-gray-400">
        <div>
          Showing {pagedItems.length} of {filteredItems.length} matching scans
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={currentPage <= 1}
            className="px-2 py-1 rounded border border-gray-700 disabled:opacity-50"
          >
            Prev
          </button>
          <span>Page {currentPage} of {pageCount}</span>
          <button
            onClick={() => setPage(p => Math.min(pageCount, p + 1))}
            disabled={currentPage >= pageCount}
            className="px-2 py-1 rounded border border-gray-700 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  )
}

function StatCard({ label, value, subtitle }: { label: string; value: number; subtitle: string }) {
  return (
    <div className="bg-gray-900 p-4 rounded-lg border border-gray-700">
      <div className="text-xs uppercase tracking-wide text-gray-400">{label}</div>
      <div className="text-3xl font-bold text-blue-300">{value}</div>
      <div className="text-xs text-gray-500">{subtitle}</div>
    </div>
  )
}

const formatHarnessSummary = (stage?: HarnessStageSummary | null) => {
  if (!stage) return 'Pending'
  if (stage.status === 'disabled') return 'Disabled'
  if (stage.status === 'skipped') return 'No targets'
  const total = stage.totalParameters ?? 0
  if (total === 0) return stage.status
  const completed = stage.completed ?? 0
  const reflections = stage.reflections ?? 0
  return `${completed}/${total} params · ${reflections} refl`
}

const formatPayloadSummary = (stage?: PayloadStageSummary | null) => {
  if (!stage) return 'Pending'
  if (stage.status === 'disabled') return 'Disabled'
  if (stage.status === 'skipped') return 'Not run'
  const total = stage.totalAttempts ?? 0
  if (total === 0) return stage.status
  const successes = stage.successes ?? 0
  return `${successes}/${total} payloads`
}

const stageStatusClass = (status: StageStatus) => {
  switch (status) {
    case 'completed':
      return 'bg-green-900/70 text-green-100 border border-green-500/40'
    case 'partial':
    case 'running':
      return 'bg-blue-900/60 text-blue-100 border border-blue-500/40'
    case 'failed':
      return 'bg-red-900/70 text-red-100 border border-red-500/40'
    case 'skipped':
    case 'disabled':
      return 'bg-gray-900/60 text-gray-400 border border-gray-700/70'
    case 'pending':
    default:
      return 'bg-gray-900/40 text-gray-300 border border-gray-700/40'
  }
}

const buildStageTitle = (label: string, stage?: HarnessStageSummary | PayloadStageSummary | null, summary?: string) => {
  const parts = [`${label} status: ${stage?.status ?? 'pending'}`]
  if (summary) parts.push(summary)
  if (stage && 'errors' in (stage as HarnessStageSummary)) {
    const harnessStage = stage as HarnessStageSummary
    const errors = Array.isArray(harnessStage.errors) ? harnessStage.errors : []
    if (errors.length) {
      const preview = errors.slice(0, 3).map(err => `${err?.parameter || 'param'}: ${err?.error || 'error'}`)
      parts.push(`Errors: ${preview.join('; ')}`)
      if (errors.length > 3) {
        parts.push(`(+${errors.length - 3} more)`)
      }
    }
  }
  return parts.join(' • ')
}

function StageBadge({ label, stage, summary, onRerun }: { label: string; stage?: HarnessStageSummary | PayloadStageSummary | null; summary?: string; onRerun?: () => void }) {
  const status = stage?.status ?? 'pending'
  const canRerun = status === 'failed' || status === 'skipped' || status === 'completed'

  return (
    <div className="flex items-center gap-1">
      <div
        className={`flex-1 flex items-center justify-between rounded px-2 py-1 text-[11px] font-medium ${stageStatusClass(status)}`}
        title={buildStageTitle(label, stage, summary)}
      >
        <span className="uppercase tracking-wide">{label}</span>
        <span className="text-[10px] font-semibold">{summary || status}</span>
      </div>
      {canRerun && onRerun && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            onRerun()
          }}
          className="p-1 rounded hover:bg-gray-700 text-gray-400 hover:text-blue-300"
          title={`Rerun ${label}`}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74-2.74L3 12" />
            <path d="M3 3v9h9" />
          </svg>
        </button>
      )}
    </div>
  )
}
